import { useState, useMemo, useEffect } from "react";
import { ArrowLeft, Minus, Plus, ShoppingCart, Battery, Check, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import type { Product, ProductVariant } from "@/hooks/useProducts";
import { supabase } from "@/integrations/supabase/client";

interface ProductDetailProps {
  product: Product;
  onBack: () => void;
}

const CONDITION_LABELS: Record<string, { label: string; description: string }> = {
  "A+": { label: "Come Nuovo", description: "Nessun segno di usura visibile" },
  "A": { label: "Eccellente", description: "Minimi segni di usura, quasi impercettibili" },
  "B": { label: "Ottimo", description: "Leggeri segni di usura, perfettamente funzionante" },
  "C": { label: "Buono", description: "Segni di usura visibili, funzionante al 100%" },
};

export const ProductDetail = ({ product, onBack }: ProductDetailProps) => {
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [selectedMemory, setSelectedMemory] = useState<number | null>(null);
  const [selectedCondition, setSelectedCondition] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [imageFade, setImageFade] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({
    name: "", email: "", phone: "", cardNumber: "", expiry: "", cvv: "",
  });
  const [processing, setProcessing] = useState(false);

  // Get unique options from variants
  const colors = useMemo(() => {
    const colorMap = new Map<string, string>();
    product.variants?.forEach(v => {
      if (!colorMap.has(v.color)) colorMap.set(v.color, v.color_hex || '#000000');
    });
    return Array.from(colorMap.entries()).map(([name, hex]) => ({ name, hex }));
  }, [product.variants]);

  const memories = useMemo(() =>
    [...new Set(product.variants?.filter(v => v.memory_gb).map(v => v.memory_gb) || [])].sort((a, b) => (a || 0) - (b || 0)),
    [product.variants]
  );

  const conditions = useMemo(() =>
    [...new Set(product.variants?.map(v => v.condition_grade) || [])].sort(),
    [product.variants]
  );

  // Auto-select first options
  useMemo(() => {
    if (!selectedColor && colors.length > 0) setSelectedColor(colors[0].name);
    if (!selectedMemory && memories.length > 0) setSelectedMemory(memories[0]);
    if (!selectedCondition && conditions.length > 0) setSelectedCondition(conditions[0]);
  }, [colors, memories, conditions]);

  // Find matching variant
  const selectedVariant = useMemo(() => {
    if (!selectedColor || !selectedCondition) return null;
    return product.variants?.find(v =>
      v.color === selectedColor &&
      v.condition_grade === selectedCondition &&
      (memories.length === 0 || v.memory_gb === selectedMemory)
    ) || null;
  }, [product.variants, selectedColor, selectedMemory, selectedCondition, memories]);

  // Calculate final price
  const baseDiscountedPrice = product.base_price * (1 - product.discount_percentage / 100);
  const finalPrice = useMemo(() => {
    const adjustment = selectedVariant?.price_adjustment || 0;
    return (baseDiscountedPrice + adjustment) * quantity;
  }, [baseDiscountedPrice, selectedVariant, quantity]);

  // Price breakdown: compute per-dimension deltas
  const priceBreakdown = useMemo(() => {
    const variants = product.variants || [];
    if (variants.length === 0 || !selectedVariant) return null;

    const findVariant = (color: string, mem: number | null, cond: string) =>
      variants.find(v => v.color === color && v.memory_gb === mem && v.condition_grade === cond);

    const baseColor = colors[0]?.name;
    const baseMem = memories.length > 0 ? memories[0] : null;
    const baseCond = conditions[0];
    if (!baseColor || !baseCond) return null;

    const baseVariant = findVariant(baseColor, baseMem, baseCond);
    const baseAdj = baseVariant?.price_adjustment || 0;

    let capacityDelta = 0;
    if (selectedMemory && baseMem !== null && selectedMemory !== baseMem) {
      const capVariant = findVariant(baseColor, selectedMemory, baseCond);
      capacityDelta = (capVariant?.price_adjustment || 0) - baseAdj;
    }

    let colorDelta = 0;
    if (selectedColor && selectedColor !== baseColor) {
      const colorVariant = findVariant(selectedColor, baseMem, baseCond);
      colorDelta = (colorVariant?.price_adjustment || 0) - baseAdj;
    }

    let conditionDelta = 0;
    if (selectedCondition && selectedCondition !== baseCond) {
      const condVariant = findVariant(baseColor, baseMem, selectedCondition);
      conditionDelta = (condVariant?.price_adjustment || 0) - baseAdj;
    }

    const totalAdj = selectedVariant.price_adjustment || 0;
    const remainder = totalAdj - baseAdj - capacityDelta - colorDelta - conditionDelta;

    return { capacityDelta, colorDelta, conditionDelta, remainder, baseAdj };
  }, [product.variants, selectedVariant, selectedColor, selectedMemory, selectedCondition, colors, memories, conditions]);

  // Images filtered by selected color (with fallback to general/master images)
  const allImages = product.images?.sort((a, b) => a.sort_order - b.sort_order) || [];
  
  const displayImages = useMemo(() => {
    const colorImages = selectedColor
      ? allImages.filter(img => img.variant_color === selectedColor)
      : [];
    const generalImages = allImages.filter(img => !img.variant_color);
    const result = colorImages.length > 0 ? colorImages : generalImages;
    
    if (result.length > 0) return result;
    return product.cover_image
      ? [{ id: '0', image_url: product.cover_image, is_cover: true, sort_order: 0, variant_color: null } as any]
      : [];
  }, [allImages, selectedColor, product.cover_image]);

  // Reset image index and trigger fade when color changes
  useEffect(() => {
    setImageFade(true);
    setSelectedImageIndex(0);
    const timer = setTimeout(() => setImageFade(false), 200);
    return () => clearTimeout(timer);
  }, [selectedColor]);

  const canAddToCart = selectedColor && selectedCondition && (memories.length === 0 || selectedMemory) && selectedVariant && selectedVariant.quantity >= quantity;
  const isOutOfStock = selectedVariant && selectedVariant.quantity === 0;

  const handleCheckout = async () => {
    if (!checkoutForm.name || !checkoutForm.email || !checkoutForm.phone || !checkoutForm.cardNumber) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    setProcessing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user && selectedVariant) {
        await supabase.from("orders").insert({
          user_id: user.id, product_id: product.id, variant_id: selectedVariant.id,
          quantity, total_price: finalPrice, status: "confirmed"
        });
      }
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast.success("Ordine confermato! Riceverai una email di conferma.");
      setShowCheckout(false);
      setCheckoutForm({ name: "", email: "", phone: "", cardNumber: "", expiry: "", cvv: "" });
    } catch (error) {
      toast.error("Errore durante l'elaborazione dell'ordine");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-semibold text-foreground truncate">{product.name}</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Image Gallery */}
          <div className="space-y-4">
            <div className={`aspect-square bg-muted rounded-2xl overflow-hidden transition-opacity duration-200 ${imageFade ? 'opacity-0' : 'opacity-100'}`}>
              {displayImages[selectedImageIndex] ? (
                <img src={displayImages[selectedImageIndex].image_url} alt={product.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">Nessuna immagine</div>
              )}
            </div>
            {displayImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {displayImages.map((img, index) => (
                  <button key={img.id} onClick={() => setSelectedImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${selectedImageIndex === index ? 'border-primary' : 'border-border'}`}>
                    <img src={img.image_url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                {product.category && <Badge variant="outline">{product.category.name}</Badge>}
                {product.discount_percentage > 0 && <Badge className="bg-destructive">-{product.discount_percentage}%</Badge>}
              </div>
              <h2 className="text-3xl font-bold text-foreground mb-2">{product.name}</h2>
              {product.description && <p className="text-muted-foreground">{product.description}</p>}
            </div>

            {/* Price */}
            <div className="space-y-3">
              <div className="flex items-baseline gap-3">
                <span className="text-4xl font-bold text-foreground">€{(finalPrice / quantity).toFixed(2)}</span>
                {product.discount_percentage > 0 && (
                  <span className="text-xl text-muted-foreground line-through">€{product.base_price.toFixed(2)}</span>
                )}
                {quantity > 1 && (
                  <span className="text-sm text-muted-foreground">× {quantity} = €{finalPrice.toFixed(2)}</span>
                )}
              </div>

              {/* Price Breakdown */}
              {priceBreakdown && selectedVariant && (
                <div className="p-3 bg-muted/40 rounded-xl space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Prezzo base</span>
                    <span>€{baseDiscountedPrice.toFixed(2)}</span>
                  </div>
                  {selectedMemory && memories.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Capacità {selectedMemory}GB</span>
                      <span className={priceBreakdown.capacityDelta > 0 ? 'text-destructive' : priceBreakdown.capacityDelta < 0 ? 'text-green-600' : ''}>
                        {priceBreakdown.capacityDelta >= 0 ? '+' : ''}€{priceBreakdown.capacityDelta.toFixed(2)}
                      </span>
                    </div>
                  )}
                  {selectedColor && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Colore: {selectedColor}</span>
                      <span className={priceBreakdown.colorDelta > 0 ? 'text-destructive' : priceBreakdown.colorDelta < 0 ? 'text-green-600' : ''}>
                        {priceBreakdown.colorDelta >= 0 ? '+' : ''}€{priceBreakdown.colorDelta.toFixed(2)}
                      </span>
                    </div>
                  )}
                  {selectedCondition && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Condizione: {CONDITION_LABELS[selectedCondition]?.label || selectedCondition}</span>
                      <span className={priceBreakdown.conditionDelta > 0 ? 'text-destructive' : priceBreakdown.conditionDelta < 0 ? 'text-green-600' : ''}>
                        {priceBreakdown.conditionDelta >= 0 ? '+' : ''}€{priceBreakdown.conditionDelta.toFixed(2)}
                      </span>
                    </div>
                  )}
                  {priceBreakdown.remainder !== 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Adeguamento combinato</span>
                      <span className={priceBreakdown.remainder > 0 ? 'text-destructive' : 'text-green-600'}>
                        {priceBreakdown.remainder >= 0 ? '+' : ''}€{priceBreakdown.remainder.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between pt-1.5 border-t border-border font-semibold">
                    <span>Totale unitario</span>
                    <span>€{(finalPrice / quantity).toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Memory Selection - Pill Style */}
            {memories.length > 0 && (
              <div className="space-y-3">
                <Label className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Capacità</Label>
                <div className="flex flex-wrap gap-2">
                  {memories.map(mem => {
                    const isSelected = selectedMemory === mem;
                    return (
                      <button
                        key={mem}
                        onClick={() => setSelectedMemory(mem)}
                        className={`px-5 py-2.5 rounded-full text-sm font-medium border-2 transition-all ${
                          isSelected
                            ? 'border-primary bg-primary text-primary-foreground shadow-md'
                            : 'border-border bg-background text-foreground hover:border-primary/50'
                        }`}
                      >
                        {mem} GB
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Color Selection - Swatches */}
            {colors.length > 0 && (
              <div className="space-y-3">
                <Label className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Colore {selectedColor && <span className="normal-case font-normal ml-1">— {selectedColor}</span>}
                </Label>
                <div className="flex flex-wrap gap-3">
                  {colors.map(color => {
                    const isSelected = selectedColor === color.name;
                    return (
                      <button
                        key={color.name}
                        onClick={() => setSelectedColor(color.name)}
                        className={`w-10 h-10 rounded-full border-2 transition-all flex items-center justify-center ${
                          isSelected ? 'border-primary ring-2 ring-primary/30 scale-110' : 'border-border hover:scale-105'
                        }`}
                        style={{ backgroundColor: color.hex }}
                        title={color.name}
                      >
                        {isSelected && (
                          <Check className={`w-4 h-4 ${
                            // Use white checkmark on dark colors
                            parseInt(color.hex.replace('#', ''), 16) < 0x888888 ? 'text-white' : 'text-black'
                          }`} />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Condition Selection - Cards */}
            {conditions.length > 0 && (
              <div className="space-y-3">
                <Label className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Condizione</Label>
                <div className="grid grid-cols-2 gap-2">
                  {conditions.map(cond => {
                    const isSelected = selectedCondition === cond;
                    const info = CONDITION_LABELS[cond] || { label: cond, description: "" };
                    return (
                      <button
                        key={cond}
                        onClick={() => setSelectedCondition(cond)}
                        className={`p-3 rounded-xl border-2 text-left transition-all ${
                          isSelected
                            ? 'border-primary bg-primary/5 shadow-sm'
                            : 'border-border hover:border-primary/30'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm">{info.label}</span>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">{cond}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{info.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Variant Info */}
            {selectedVariant && (
              <Card className="bg-muted/50 border-border/50">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 text-sm">
                      <Battery className="w-4 h-4 text-green-500" />
                      <span>Batteria: <strong>{selectedVariant.battery_percentage}%</strong></span>
                    </div>
                    <div className="w-px h-4 bg-border" />
                    <div className="flex items-center gap-1 text-sm">
                      <ShieldCheck className="w-4 h-4 text-primary" />
                      <span>Garanzia 12 mesi</span>
                    </div>
                  </div>
                  <Badge variant={selectedVariant.quantity > 0 ? "outline" : "destructive"}>
                    {selectedVariant.quantity > 0 ? `${selectedVariant.quantity} disponibili` : 'Esaurito'}
                  </Badge>
                </CardContent>
              </Card>
            )}

            {/* Quantity */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Quantità</Label>
              <div className="flex items-center gap-3">
                <Button variant="outline" size="icon" onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={quantity <= 1}>
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="w-12 text-center font-semibold text-lg">{quantity}</span>
                <Button variant="outline" size="icon" onClick={() => setQuantity(quantity + 1)} disabled={!selectedVariant || quantity >= selectedVariant.quantity}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Add to Cart */}
            <Button
              className="w-full h-14 text-lg gap-2 rounded-xl"
              disabled={!canAddToCart || isOutOfStock}
              onClick={() => setShowCheckout(true)}
            >
              <ShoppingCart className="w-5 h-5" />
              {isOutOfStock ? 'Non disponibile' : 'Procedi all\'ordine'}
            </Button>
          </div>
        </div>
      </div>

      {/* Checkout Dialog */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Completa l'ordine</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-muted rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{product.name}</span>
                <span className="font-medium">x{quantity}</span>
              </div>
              {selectedVariant && (
                <div className="text-sm text-muted-foreground">
                  {selectedColor} • {selectedMemory && `${selectedMemory}GB • `}
                  {CONDITION_LABELS[selectedCondition || '']?.label || `Grado ${selectedCondition}`}
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-border">
                <span className="font-semibold">Totale</span>
                <span className="font-bold text-lg">€{finalPrice.toFixed(2)}</span>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <Label htmlFor="name">Nome completo *</Label>
                <Input id="name" value={checkoutForm.name} onChange={e => setCheckoutForm({ ...checkoutForm, name: e.target.value })} placeholder="Mario Rossi" />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input id="email" type="email" value={checkoutForm.email} onChange={e => setCheckoutForm({ ...checkoutForm, email: e.target.value })} placeholder="mario@email.com" />
              </div>
              <div>
                <Label htmlFor="phone">Numero di telefono *</Label>
                <Input id="phone" type="tel" value={checkoutForm.phone} onChange={e => setCheckoutForm({ ...checkoutForm, phone: e.target.value })} placeholder="+39 333 1234567" />
              </div>
              <div>
                <Label htmlFor="card">Numero carta *</Label>
                <Input id="card" value={checkoutForm.cardNumber} onChange={e => setCheckoutForm({ ...checkoutForm, cardNumber: e.target.value })} placeholder="4242 4242 4242 4242" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="expiry">Scadenza</Label>
                  <Input id="expiry" value={checkoutForm.expiry} onChange={e => setCheckoutForm({ ...checkoutForm, expiry: e.target.value })} placeholder="MM/AA" />
                </div>
                <div>
                  <Label htmlFor="cvv">CVV</Label>
                  <Input id="cvv" value={checkoutForm.cvv} onChange={e => setCheckoutForm({ ...checkoutForm, cvv: e.target.value })} placeholder="123" />
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground text-center">I tuoi dati sono protetti con crittografia SSL</p>
            <Button className="w-full" onClick={handleCheckout} disabled={processing}>
              {processing ? "Elaborazione..." : "Conferma ordine"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
